import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { providerLabel } from "@/components/AgentNode";

export default function AgentDrawer({ agent, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {agent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 flex justify-end"
          onClick={onClose}
          data-testid="agent-drawer-backdrop"
        >
          <div className="absolute inset-0 bg-[#FFF]/30 backdrop-blur-[2px]" />
          <motion.aside
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 60, opacity: 0 }}
            transition={{ type: "tween", duration: 0.28 }}
            className="relative w-full max-w-xl bg-[#010f23] border-l hairline-strong h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid={`agent-drawer-${agent.id}`}
          >
            <div className="p-8 lg:p-10">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="label-xs mb-2">
                    Layer {String(agent.layer).padStart(2,"0")} · {agent.layer_name}
                  </div>
                  <h3 className="font-serif-display text-4xl italic leading-tight">{agent.name}</h3>
                </div>
                <button
                  onClick={onClose}
                  className="font-mono text-xs uppercase tracking-[0.22em] underline underline-offset-4 hover:text-[#00B4E6]"
                  data-testid="agent-drawer-close"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-2 border hairline mb-6">
                <div className="p-4 border-r hairline">
                  <div className="label-xs">Model</div>
                  <div className="font-mono text-sm mt-1">{providerLabel[agent.provider] || agent.provider}</div>
                </div>
                <div className="p-4">
                  <div className="label-xs">Status</div>
                  <div className="font-mono text-sm mt-1 capitalize">{agent.status || "idle"}</div>
                </div>
              </div>

              <div className="mb-6">
                <div className="label-xs mb-2">Role</div>
                <p className="text-[15px] leading-relaxed text-[#E4EAF4]">{agent.role}</p>
              </div>

              <div>
                <div className="label-xs mb-2">Output stream</div>
                <div className="border hairline p-5 font-mono text-[13px] leading-relaxed whitespace-pre-wrap text-[#FFF] min-h-[180px] bg-[#0D2444]/60">
                  {agent.output
                    ? <>{agent.output}{(agent.status === "working" || agent.status === "thinking") && <span className="caret" />}</>
                    : <span className="text-[#6B7A94]">— awaiting dispatch —</span>}
                </div>
                {agent.error && (
                  <div className="mt-3 border-l-2 border-[#FF5B5B] pl-3 py-1 font-mono text-xs text-[#FF5B5B]" data-testid="agent-error-msg">
                    {agent.error}
                  </div>
                )}
              </div>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
